/*
  <main-product-page>
  Controller for the redesigned product page (Figma 1687-4979).
  Responsibilities:
    - Gallery: thumbnail click -> swap main image
    - Size cards: select variant -> sync form id, prices, % OFF pill,
      purchase-option prices, ATC state, URL ?variant=
    - Quantity stepper
    - Purchase options: one-time vs Subscribe & Save (selling_plan select)
    - ZIP delivery estimate (same data sources as estimated-delivery-custom.js;
      persists the shopper's ZIP in the ed_zip localStorage key)
    - Wishlist heart (window.cibFavorites)
    - Bundle upsell: fetch recommendations, checkbox selection, multi-add

  Add-to-cart submits through the theme's <product-form> (product-form.js), so
  drawer/AJAX behaviour is inherited unchanged.
*/
if (!customElements.get('main-product-page')) {
  class MainProductPage extends HTMLElement {
    connectedCallback() {
      try {
        const dataEl = this.querySelector('[data-pdp-variants]');
        this.variants = dataEl ? JSON.parse(dataEl.textContent) : {};
      } catch (e) {
        this.variants = {};
      }

      this.idInput = this.querySelector('[data-pdp-id]');
      this.qtyInput = this.querySelector('.pdp__qty-input');
      this.atc = this.querySelector('.pdp__atc');
      this.priceEl = this.querySelector('[data-pdp-price]');
      this.compareEl = this.querySelector('[data-pdp-compare]');
      this.offPill = this.querySelector('[data-pdp-off]');
      this.offNum = this.querySelector('[data-pdp-off-num]');

      this.bindGallery();
      this.bindZoom();
      this.bindSizes();
      this.bindPills();
      this.bindRating();
      this.bindQuantity();
      this.bindPurchaseOptions();
      this.bindZip();
      this.bindWishlist();
      this.bindBundle();
    }

    /* ---------- Gallery: scroll-snap slides, synced dots + thumbnails ---------- */
    bindGallery() {
      const slider = this.querySelector('[data-pdp-slides]');
      if (!slider) return;
      const slides = Array.from(slider.children);
      if (slides.length < 2) return;
      const thumbs = this.querySelectorAll('[data-pdp-thumb]');
      const dots = this.querySelectorAll('[data-pdp-dot]');

      const goTo = (index, smooth) => {
        const slide = slides[index];
        if (!slide) return;
        slider.scrollTo({ left: slide.offsetLeft, behavior: smooth === false ? 'auto' : 'smooth' });
      };

      thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => goTo(i)));
      dots.forEach((dot, i) => dot.addEventListener('click', () => goTo(i)));

      const sync = () => {
        const index = Math.round(slider.scrollLeft / Math.max(1, slider.clientWidth));
        thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
        dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      };
      slider.addEventListener('scroll', () => {
        clearTimeout(this._galleryTimer);
        this._galleryTimer = setTimeout(sync, 80);
      });

      // Start on the featured media (may not be the first one).
      const start = parseInt(slider.dataset.pdpStart, 10) || 0;
      if (start > 0) goTo(start, false);

      // Exposed for setVariant: jump to a variant's featured media.
      this.showMedia = (mediaId) => {
        if (!mediaId) return;
        const index = slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
        if (index >= 0) goTo(index); // the scroll listener syncs thumbs/dots
      };
    }

    /* ---------- Click zoom (per slide) ----------
       Click zooms in at the cursor, mousemove pans while zoomed, clicking
       again or leaving the image zooms out. */
    bindZoom() {
      const media = this.querySelector('[data-pdp-zoom]');
      if (!media) return;
      // Zoom only makes sense with a real pointer.
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      // Mirrors the v1 zoom levels: medium (1000px) / large (1500px) rendered
      // in a ~668px column, and "original" = the image's natural resolution.
      const LEVELS = { medium: 1.6, large: 2.2 };
      const setting = media.dataset.pdpZoom || 'medium';

      media.classList.add('pdp__main-media--zoom');

      media.querySelectorAll('.pdp__slide').forEach((slide) => {
        const img = slide.querySelector('img');
        if (!img) return;
        let zoomed = false;

        const zoomLevel = (rect) => {
          if (setting === 'original' && img.naturalWidth) {
            return Math.min(4, Math.max(1.5, img.naturalWidth / rect.width));
          }
          return LEVELS[setting] || LEVELS.medium;
        };

        const setOrigin = (e) => {
          const rect = slide.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          img.style.transformOrigin = x + '% ' + y + '%';
        };

        const reset = () => {
          if (!zoomed) return;
          zoomed = false;
          slide.classList.remove('is-zoomed');
          img.style.transform = '';
          img.style.transformOrigin = '';
        };

        slide.addEventListener('click', (e) => {
          if (zoomed) {
            reset();
            return;
          }
          zoomed = true;
          slide.classList.add('is-zoomed');
          // Nudge the browser to fetch the sharpest srcset candidate.
          img.sizes = '100vw';
          setOrigin(e);
          const rect = slide.getBoundingClientRect();
          img.style.transform = 'scale(' + zoomLevel(rect) + ')';
        });

        slide.addEventListener('mousemove', (e) => {
          if (zoomed) setOrigin(e);
        });

        slide.addEventListener('mouseleave', reset);
      });
    }

    /* ---------- Rating: smooth-scroll to the reviews widget ---------- */
    bindRating() {
      // Both the stars under the title and the "Reviews" row below the
      // details accordions jump to the reviews widget.
      const ratings = this.querySelectorAll('[data-pdp-rating]');
      if (!ratings.length) return;
      // The Judge.me widget container is server-rendered further down the page.
      const target = document.querySelector('.jdgm-rev-widg, .jdgm-review-widget, .jdgm-widget');
      if (!target) return;
      ratings.forEach((rating) => {
        rating.classList.add('is-link');
        rating.addEventListener('click', () => {
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
    }

    /* ---------- Variations: collapse to two rows + "+N more" toggle ----------
       The toggle lives INSIDE the pill flow as the last item of row two
       (Amazon-style): trailing pills are hidden until everything, toggle
       included, fits in two rows. */
    bindPills() {
      const wrap = this.querySelector('.pdp__pills');
      if (!wrap || wrap.children.length < 3) return;

      const pills = Array.from(wrap.children);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pdp__pills-toggle';
      wrap.appendChild(btn);

      // Number of rows currently occupied by the visible children.
      const rowCount = () => {
        const base = wrap.getBoundingClientRect().top;
        const tops = [];
        Array.from(wrap.children).forEach((el) => {
          if (el.hidden || el.style.display === 'none') return;
          const t = Math.round(el.getBoundingClientRect().top - base);
          if (tops.indexOf(t) === -1) tops.push(t);
        });
        return tops.length;
      };

      let expanded = false;

      const apply = () => {
        // Reset: everything visible, toggle hidden.
        pills.forEach((pill) => {
          pill.style.display = '';
        });
        btn.hidden = true;

        if (rowCount() <= 2) return; // fits naturally — no toggle needed

        btn.hidden = false;
        if (expanded) {
          btn.textContent = 'Show less';
          return;
        }

        // Widest possible label first so the fit measurement is safe, then
        // hide trailing pills until two rows hold everything.
        btn.textContent = '+' + pills.length + ' more';
        for (let i = pills.length - 1; i >= 0 && rowCount() > 2; i--) {
          pills[i].style.display = 'none';
        }
        const hiddenCount = pills.filter((pill) => pill.style.display === 'none').length;
        btn.textContent = '+' + hiddenCount + ' more';
      };

      btn.addEventListener('click', () => {
        expanded = !expanded;
        apply();
      });

      // Row breaks move with viewport width and after webfonts settle.
      window.addEventListener('resize', () => {
        clearTimeout(this._pillsTimer);
        this._pillsTimer = setTimeout(apply, 150);
      });
      window.addEventListener('load', apply, { once: true });

      apply();
    }

    /* ---------- Variant (size cards) ---------- */
    bindSizes() {
      const sizes = this.querySelectorAll('[data-pdp-size]');
      sizes.forEach((card) => {
        card.addEventListener('click', () => {
          if (card.classList.contains('is-active')) return;
          sizes.forEach((c) => {
            const active = c === card;
            c.classList.toggle('is-active', active);
            c.setAttribute('aria-checked', active ? 'true' : 'false');
          });
          this.setVariant(card.dataset.variantId);
        });
      });
    }

    setVariant(variantId) {
      const v = this.variants[variantId];
      if (!v) return;

      if (this.idInput) {
        this.idInput.value = variantId;
        // Notify listeners bound to [name="id"] (e.g. variant-title.js).
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Variant image: scroll the gallery to this variant's featured media.
      if (this.showMedia && v.mediaId) this.showMedia(v.mediaId);

      if (this.priceEl) this.priceEl.textContent = v.price;
      if (this.compareEl) {
        if (v.compare) {
          this.compareEl.textContent = v.compare;
          this.compareEl.hidden = false;
        } else {
          this.compareEl.hidden = true;
        }
      }
      if (this.offPill) {
        if (v.savePercent > 0) {
          if (this.offNum) this.offNum.textContent = v.savePercent;
          this.offPill.hidden = false;
        } else {
          this.offPill.hidden = true;
        }
      }

      // Purchase option prices
      const onetime = this.querySelector('[data-pdp-onetime-price]');
      if (onetime) onetime.textContent = v.price;
      const subs = this.querySelector('[data-pdp-subs-price]');
      if (subs && v.subsPrice) subs.textContent = v.subsPrice;
      const badge = this.querySelector('[data-pdp-subs-badge]');
      if (badge && !badge.dataset.customText && v.subsPercent > 0) {
        badge.textContent = 'SAVE ' + v.subsPercent + '%';
        badge.hidden = false;
      }

      // ATC state
      if (this.atc) {
        this.atc.toggleAttribute('disabled', !v.available);
        if (!v.available) {
          this.atc.setAttribute('aria-disabled', 'true');
          this.atc.textContent = 'Sold out';
        } else {
          this.atc.removeAttribute('aria-disabled');
          this.atc.textContent = this.atc.dataset.addToCartText || 'Add to Cart';
        }
      }

      // Keep the URL shareable
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
      } catch (e) {
        /* no-op */
      }

      this.updateBundleTotals();
    }

    /* ---------- Quantity ---------- */
    bindQuantity() {
      if (!this.qtyInput) return;
      const min = parseInt(this.qtyInput.min, 10) || 1;

      this.querySelectorAll('[data-pdp-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const step = btn.dataset.pdpStep === 'down' ? -1 : 1;
          const current = parseInt(this.qtyInput.value, 10) || min;
          this.qtyInput.value = Math.max(min, current + step);
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });

      this.qtyInput.addEventListener('change', () => {
        const val = parseInt(this.qtyInput.value, 10);
        if (Number.isNaN(val) || val < min) this.qtyInput.value = min;
        this.updateBundleTotals();
      });
    }

    /* ---------- Purchase options ---------- */
    bindPurchaseOptions() {
      const wrap = this.querySelector('[data-pdp-purchase]');
      if (!wrap) return;
      const rows = wrap.querySelectorAll('.pdp__purchase-row');
      const freq = wrap.querySelector('[data-pdp-freq]');
      const planSelect = wrap.querySelector('[data-pdp-plan-select]');
      const badge = wrap.querySelector('[data-pdp-subs-badge]');
      if (badge && badge.textContent.trim() && !/^SAVE \d+%$/.test(badge.textContent.trim())) {
        badge.dataset.customText = 'true';
      }

      // The (hidden) Seal Subscriptions widget keeps its own selling_plan
      // field inside the form; on submit both would post and the app's empty
      // one wins, silently downgrading the add to a one-time purchase.
      // Disable every selling_plan field that isn't ours.
      const neutralizeForeignPlanFields = () => {
        if (!planSelect || !planSelect.form) return;
        Array.from(planSelect.form.elements).forEach((el) => {
          if (el.name === 'selling_plan' && el !== planSelect) {
            el.disabled = true;
          }
        });
      };

      wrap.querySelectorAll('.pdp__purchase-radio').forEach((radio) => {
        radio.addEventListener('change', () => {
          const subscribe = radio.value === 'subscribe' && radio.checked;
          rows.forEach((row) => {
            row.classList.toggle('is-active', row.dataset.purchase === (subscribe ? 'subscribe' : 'onetime'));
          });
          if (freq) freq.hidden = !subscribe;
          // Disabled selects are not submitted -> one-time purchase.
          if (planSelect) planSelect.disabled = !subscribe;
          neutralizeForeignPlanFields();
        });
      });

      planSelect?.addEventListener('change', () => {
        planSelect.classList.remove('is-error');
      });

      // Validate + clean up right before the form submits (click fires
      // before submit, ahead of product-form.js's handler).
      const atc = this.querySelector('.pdp__atc');
      if (atc && planSelect) {
        atc.addEventListener('click', (e) => {
          neutralizeForeignPlanFields();
          if (!planSelect.disabled && !planSelect.value) {
            e.preventDefault();
            if (freq) freq.hidden = false;
            planSelect.classList.add('is-error');
            planSelect.focus();
          }
        });
      }

      // The app injects its field asynchronously — sweep once things settle.
      window.addEventListener('load', neutralizeForeignPlanFields, { once: true });
      setTimeout(neutralizeForeignPlanFields, 2500);
    }

    /* ---------- ZIP delivery estimate ---------- */
    bindZip() {
      const zip = this.querySelector('[data-pdp-zip]');
      if (!zip) return;

      const input = zip.querySelector('[data-pdp-zip-input]');
      const check = zip.querySelector('[data-pdp-zip-check]');
      const row = zip.querySelector('[data-pdp-zip-row]');
      const hint = zip.querySelector('[data-pdp-zip-hint]');
      const error = zip.querySelector('[data-pdp-zip-error]');
      const orderLine = zip.querySelector('[data-pdp-zip-order]');
      const locationBtn = zip.querySelector('[data-pdp-zip-location]');
      const locationText = zip.querySelector('[data-pdp-zip-location-text]');
      const cutoff = zip.dataset.cutoff || '12:00';
      const maxDays = parseInt(zip.dataset.maxdays, 10) || 1;

      // Once a ZIP is known the input collapses behind a "City - 12345 v"
      // indicator, like the original estimated-delivery block.
      const showForm = (open) => {
        if (row) row.hidden = !open;
        if (locationBtn) locationBtn.classList.toggle('is-open', open);
        // While re-entering the ZIP, the meta row (countdown + indicator)
        // steps aside; it returns when a valid ZIP is submitted.
        zip.classList.toggle('is-editing', open);
        if (open && input) input.focus();
      };

      // UPS Ground average transit days by state (NJ origin) — mirrors
      // estimated-delivery-custom.js so both pages estimate identically.
      const stateTransitDays = {
        NJ: 2, NY: 2,
        CT: 3, PA: 3, DE: 3, MD: 3, VA: 3, WV: 3, OH: 3, DC: 3, MA: 3, RI: 3, NH: 3, VT: 3,
        ME: 4, MI: 4, IN: 4, KY: 4, TN: 4, NC: 4, SC: 4, GA: 4, FL: 4, AL: 4, MS: 4, LA: 4, AR: 4, MO: 4, IL: 4, WI: 4, MN: 4, IA: 4,
        ND: 5, SD: 5, NE: 5, KS: 5, OK: 5, CO: 5, NM: 5, AZ: 5, UT: 5, NV: 5, TX: 5,
        CA: 6, OR: 6, WA: 6, ID: 6, MT: 6, WY: 6,
        AK: 8, HI: 8
      };

      const renderOrderWithin = () => {
        const now = new Date();
        const dow = now.getDay();
        if (dow === 0 || dow === 6) {
          orderLine.innerHTML = 'Ships <strong>Monday</strong>';
          return;
        }
        const parts = cutoff.split(':').map(Number);
        let c = new Date(now);
        c.setHours(parts[0] || 12, parts[1] || 0, 0, 0);
        if (now >= c) c.setDate(c.getDate() + 1);
        const mins = Math.floor((c - now) / 60000);
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        orderLine.innerHTML = 'Order within <strong>' + h + ' hrs ' + m + ' mins.</strong>';
      };

      const getShipDate = () => {
        const now = new Date();
        const parts = cutoff.split(':').map(Number);
        const ship = new Date(now);
        if (now.getHours() > parts[0] || (now.getHours() === parts[0] && now.getMinutes() >= (parts[1] || 0))) {
          ship.setDate(ship.getDate() + 1);
        }
        while (ship.getDay() === 0 || ship.getDay() === 6) ship.setDate(ship.getDate() + 1);
        return ship;
      };

      const addBusinessDays = (date, days) => {
        const result = new Date(date);
        let added = 0;
        while (added < days) {
          result.setDate(result.getDate() + 1);
          if (result.getDay() !== 0 && result.getDay() !== 6) added++;
        }
        return result;
      };

      const estimate = async (val) => {
        error.hidden = true;
        let state = '';
        let city = '';
        try {
          const res = await fetch('https://api.zippopotam.us/us/' + val);
          if (res.ok) {
            const data = await res.json();
            if (data && data.places && data.places.length) {
              city = data.places[0]['place name'];
              state = data.places[0]['state abbreviation'];
            }
          }
        } catch (e) {
          /* fall back to maxDays */
        }

        const transit = stateTransitDays[state] || maxDays;
        const deliverDate = addBusinessDays(getShipDate(), transit);
        const oneDay = 24 * 60 * 60 * 1000;
        const now = new Date();
        let text;
        if (deliverDate - now >= oneDay && deliverDate - now < 2 * oneDay) {
          text = 'Tomorrow';
        } else {
          text = deliverDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
        }
        hint.innerHTML = 'Estimated delivery by <strong>' + text + '</strong>';

        if (locationText) locationText.textContent = (city ? city + ' - ' : '') + val;
        if (locationBtn) locationBtn.hidden = false;
      };

      const submit = () => {
        const val = (input.value || '').trim();
        if (/^[0-9]{5}$/.test(val)) {
          try {
            localStorage.setItem('ed_zip', val);
          } catch (e) { /* private mode */ }
          estimate(val);
          showForm(false);
        } else {
          error.textContent = 'Please enter a valid ZIP code.';
          error.hidden = false;
          input.focus();
        }
      };

      check.addEventListener('click', submit);
      input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          submit();
        }
      });

      if (locationBtn) {
        locationBtn.addEventListener('click', () => showForm(row ? row.hidden : true));
      }

      renderOrderWithin();
      this._zipTimer = setInterval(renderOrderWithin, 60000);

      // "12345" or "12345-6789" -> "12345"; anything else -> ''
      const toZip5 = (v) => {
        const m = /^\s*([0-9]{5})(?:-[0-9]{4})?\s*$/.exec(String(v || ''));
        return m ? m[1] : '';
      };

      const apply = (val, persist) => {
        if (persist) {
          try {
            localStorage.setItem('ed_zip', val);
          } catch (e) { /* private mode */ }
        }
        input.value = val;
        estimate(val);
        showForm(false);
      };

      // Auto-detect, in the same order as the original estimated-delivery
      // block: (1) ZIP the shopper already entered on either page version,
      // (2) the logged-in customer's default address, (3) the visitor's IP.
      let saved = '';
      try {
        saved = localStorage.getItem('ed_zip') || '';
      } catch (e) { /* private mode */ }
      saved = toZip5(saved);

      const customerZip = toZip5(zip.dataset.customerZip);

      if (saved) {
        apply(saved, false);
      } else if (customerZip) {
        apply(customerZip, true);
      } else {
        // Geo-IP: try ipapi.co first (what v1 used), then ipwho.is, so a
        // single blocked or rate-limited provider doesn't leave the field
        // empty. Only US 5-digit results count; otherwise the form stays
        // open for manual entry. Silent on failure.
        const providers = [
          { url: 'https://ipapi.co/json/', pick: (d) => d && d.country_code === 'US' && d.postal },
          { url: 'https://ipwho.is/', pick: (d) => d && d.success !== false && d.country_code === 'US' && d.postal }
        ];
        const tryProvider = (i) => {
          if (i >= providers.length || input.value.trim()) return;
          const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
          const timer = ctrl ? setTimeout(() => ctrl.abort(), 5000) : null;
          fetch(providers[i].url, ctrl ? { signal: ctrl.signal } : undefined)
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((data) => {
              const geo = toZip5(providers[i].pick(data));
              if (geo) apply(geo, true);
              else tryProvider(i + 1);
            })
            .catch(() => tryProvider(i + 1))
            .finally(() => { if (timer) clearTimeout(timer); });
        };
        tryProvider(0);
      }
    }

    /* ---------- Wishlist ---------- */
    bindWishlist() {
      const wish = this.querySelector('[data-pdp-wish]');
      if (!wish) return;
      const productId = this.dataset.productId;

      const sync = () => {
        if (window.cibFavorites && productId) {
          const active = window.cibFavorites.has(productId);
          wish.classList.toggle('is-active', !!active);
          wish.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
      };
      sync();

      this._onFavoritesChange = (e) => {
        const d = e.detail || {};
        if (d.productId === null || String(d.productId) === String(productId)) sync();
      };
      document.addEventListener('cib:favorites:change', this._onFavoritesChange);

      wish.addEventListener('click', () => {
        if (window.cibFavorites && productId) {
          const active = window.cibFavorites.toggle(productId);
          wish.classList.toggle('is-active', !!active);
          wish.setAttribute('aria-pressed', active ? 'true' : 'false');
        } else {
          const active = wish.classList.toggle('is-active');
          wish.setAttribute('aria-pressed', active ? 'true' : 'false');
        }
      });
    }

    /* ---------- Bundle upsell ---------- */
    bindBundle() {
      const bundle = this.querySelector('[data-pdp-bundle]');
      if (!bundle) return;
      this.bundle = bundle;
      this.bundleItems = [];

      const recsUrl = bundle.dataset.recsUrl || '/recommendations/products';
      const url = recsUrl + '.json?product_id=' + this.dataset.productId + '&limit=3&intent=related';

      fetch(url, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          const products = (data && data.products) || [];
          if (!products.length) return; // keep hidden
          this.renderBundle(products);
          bundle.hidden = false;
        })
        .catch(() => { /* keep hidden */ });
    }

    formatMoney(cents) {
      const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      try {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency }).format(cents / 100);
      } catch (e) {
        return '$' + (cents / 100).toFixed(2);
      }
    }

    renderBundle(products) {
      const list = this.bundle.querySelector('[data-pdp-bundle-list]');
      list.innerHTML = '';
      this.bundleItems = [];

      products.forEach((p) => {
        const variant = (p.variants || []).find((v) => v.available) || (p.variants || [])[0];
        if (!variant) return;

        const row = document.createElement('div');
        row.className = 'pdp__bundle-row is-selected';

        const check = document.createElement('button');
        check.type = 'button';
        check.className = 'pdp__bundle-check';
        check.setAttribute('aria-label', 'Include ' + p.title + ' in bundle');
        check.setAttribute('aria-pressed', 'true');
        check.innerHTML =
          '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
          '<path d="M3.5 8.5l3 3 6-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        const img = document.createElement('img');
        img.className = 'pdp__bundle-thumb';
        img.loading = 'lazy';
        img.alt = p.title || '';
        if (p.featured_image) {
          img.src = p.featured_image + (p.featured_image.indexOf('?') === -1 ? '?width=120' : '&width=120');
        }

        const info = document.createElement('div');
        info.className = 'pdp__bundle-info';
        const name = document.createElement('a');
        name.className = 'pdp__bundle-name';
        name.href = p.url;
        name.textContent = p.title;
        info.appendChild(name);

        const prices = document.createElement('div');
        prices.className = 'pdp__bundle-prices';
        // Reference price must belong to the SAME variant whose price is
        // shown — the product-level compare_at_price is the minimum across
        // all variants and can be another variant's (or exist when this
        // variant has no discount at all).
        const wasCents = parseInt(variant.compare_at_price, 10) || 0;
        if (wasCents > variant.price) {
          const was = document.createElement('span');
          was.className = 'pdp__bundle-was';
          was.textContent = this.formatMoney(wasCents);
          prices.appendChild(was);
        }
        const price = document.createElement('span');
        price.className = 'pdp__bundle-price';
        price.textContent = this.formatMoney(variant.price);
        prices.appendChild(price);

        row.appendChild(check);
        row.appendChild(img);
        row.appendChild(info);
        row.appendChild(prices);
        list.appendChild(row);

        const item = { row: row, check: check, variantId: variant.id, priceCents: variant.price, selected: true };
        this.bundleItems.push(item);

        check.addEventListener('click', () => {
          item.selected = !item.selected;
          row.classList.toggle('is-selected', item.selected);
          check.setAttribute('aria-pressed', item.selected ? 'true' : 'false');
          check.innerHTML = item.selected
            ? '<svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
              '<path d="M3.5 8.5l3 3 6-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
            : '';
          this.updateBundleTotals();
        });
      });

      const atc = this.bundle.querySelector('[data-pdp-bundle-atc]');
      atc.addEventListener('click', () => this.addBundleToCart());

      this.updateBundleTotals();
    }

    updateBundleTotals() {
      if (!this.bundle || !this.bundleItems || !this.bundleItems.length) return;

      const variantId = this.idInput ? this.idInput.value : null;
      const v = variantId ? this.variants[variantId] : null;
      const qty = parseInt(this.qtyInput && this.qtyInput.value, 10) || 1;

      let total = v ? v.priceCents * qty : 0;
      let count = 0;
      this.bundleItems.forEach((item) => {
        if (item.selected) {
          total += item.priceCents;
          count++;
        }
      });

      const totalEl = this.bundle.querySelector('[data-pdp-bundle-total]');
      const totalBtn = this.bundle.querySelector('[data-pdp-bundle-total-btn]');
      const note = this.bundle.querySelector('[data-pdp-bundle-note]');
      const atc = this.bundle.querySelector('[data-pdp-bundle-atc]');
      const money = this.formatMoney(total);

      if (totalEl) totalEl.textContent = money;
      if (totalBtn) totalBtn.textContent = money;
      if (note) {
        note.textContent = count > 0
          ? 'Includes this product + ' + count + ' item' + (count === 1 ? '' : 's') + ' above'
          : 'Select items above to build your bundle';
      }
      if (atc) atc.disabled = count === 0 && !v;
    }

    async addBundleToCart() {
      const atc = this.bundle.querySelector('[data-pdp-bundle-atc]');
      const variantId = this.idInput ? this.idInput.value : null;
      const qty = parseInt(this.qtyInput && this.qtyInput.value, 10) || 1;

      const items = [];
      if (variantId) items.push({ id: Number(variantId), quantity: qty });
      this.bundleItems.forEach((item) => {
        if (item.selected) items.push({ id: Number(item.variantId), quantity: 1 });
      });
      if (!items.length) return;

      atc.disabled = true;
      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: items })
        });
        if (!res.ok) throw new Error('Add to cart failed');
        if (window.cibCart) window.cibCart.invalidate();
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { bubbles: true }));
      } catch (e) {
        console.warn('[Main product] bundle add failed', e);
      } finally {
        atc.disabled = false;
      }
    }

    disconnectedCallback() {
      if (this._zipTimer) clearInterval(this._zipTimer);
      if (this._onFavoritesChange) {
        document.removeEventListener('cib:favorites:change', this._onFavoritesChange);
        this._onFavoritesChange = null;
      }
    }
  }

  customElements.define('main-product-page', MainProductPage);
}
